import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { driver, DriveStep, Driver } from 'driver.js';
import { TranslationService } from 'src/app/i18n/translation.service';
import { AppConstants } from 'src/app/commons/app-constants';

export type FeatureTourKey =
  | 'header'
  | 'shipments'
  | 'tallysheet'
  | 'reports'
  | 'business-partner'
  | 'products'
  | 'loading-site'
  | 'users'
  | 'dashboard-v7'
  | 'inventory-overview'
  | 'inventory-in-out'
  | 'inventory-processing'
  | 'inventory-operators'
  | 'rbac-settings'
  | 'subscription-management';

/** Old browser-only "seen" marks. Now only read once, to carry them over to the user's account. */
const LEGACY_KEY = /^tms_tour_([a-z0-9-]+)_v1$/;
const legacyKey = (f: string) => `tms_tour_${f}_v1`;

/** GET /users/me/tours — see UserTourController. */
interface TourState {
  /** The SUPER_ADMIN/ROOT switch in App Settings (off by default). */
  enabled: boolean;
  /** SUPER_ADMIN/ROOT may replay a tour even while tours are switched off (to check it first). */
  canReplay: boolean;
  seen: Set<string>;
}

/** Pages where no tour runs and no tour call is made (not signed in / onboarding / shared views). */
const NO_TOUR_PATHS = ['/login', '/registration', '/account-setup', '/onboarding', '/email-verification',
  '/forgot-password', '/password-reset', '/user-added-to-existing-company', '/s/', '/public',
  '/tally-sheet/share', '/tallysheet-view', '/unauthorized'];

/**
 * Product tours (TOUR_PER_USER_PLAN.md, 2026-09-28): "seen" is remembered per USER on the server,
 * not per browser — a tour finished, skipped or closed once never comes back on any device.
 * A SUPER_ADMIN/ROOT switch (App Settings) turns tours on/off for everyone; while off, no tour
 * shows by itself and the Tour menu item/button is hidden (SUPER_ADMIN/ROOT can still replay).
 */
@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);

  private readonly state = signal<TourState | null>(null);
  private loading: Promise<TourState | null> | null = null;

  /** Show the Tour menu item / header button (drives the header + sidebar menus). */
  readonly replayAllowed = computed(() => {
    const s = this.state();
    return !!s && (s.enabled || s.canReplay);
  });

  // The live driver.js instance, or null when no tour is running. driver.js with `animate: true`
  // runs a requestAnimationFrame loop to keep its highlight stage positioned over the target
  // element. run() used to drop this reference on the floor and nothing ever called destroy() —
  // so navigating away from a page mid-tour (very common: the dashboard-v7 tour auto-starts 500ms
  // after load, and a quick navigation away leaves it orphaned) left that rAF loop spinning at
  // ~60fps forever, thrashing to reposition over now-removed elements. Every frame triggered an
  // Angular change-detection pass, which on a heavy screen (account-details) froze the page
  // completely (confirmed 2026-09-08: ngDoCheck firing ~57x/second with nothing else running).
  private activeDriver: Driver | null = null;

  constructor(private translationService: TranslationService) {
    // Any navigation kills a running tour — its anchors are about to disappear.
    // Going to /login (every logout / session end ends there) forgets the signed-in user's tour
    // state, so the next person to sign in on this tab loads their own.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe((e) => {
        this.stopActiveTour();
        if ((e as NavigationStart).url.startsWith('/login')) this.forget();
      });
    // Load the signed-in user's tour state after a page opens (skipped on sign-in / public pages),
    // so the Tour menu item appears even on pages that have no auto tour.
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => { if (!this.state()) void this.ensureLoaded(); });
  }

  private forget(): void {
    this.state.set(null);
    this.loading = null;
  }

  /** Re-read the switch + seen list (after an admin changes the tour switch in App Settings). */
  refresh(): void {
    this.forget();
    void this.ensureLoaded();
  }

  /** Load this user's tour state once per sign-in. Never throws: null = unknown (offline / error). */
  ensureLoaded(): Promise<TourState | null> {
    const current = this.state();
    if (current) return Promise.resolve(current);
    if (NO_TOUR_PATHS.some(p => (window.location.pathname || '').startsWith(p))) return Promise.resolve(null);
    if (!this.loading) {
      this.loading = firstValueFrom(
        this.http.get<{ enabled: boolean; canReplay: boolean; seen: string[] }>(`${AppConstants.API_URL}api/v1/users/me/tours`),
      ).then(res => {
        const s: TourState = { enabled: !!res?.enabled, canReplay: !!res?.canReplay, seen: new Set(res?.seen ?? []) };
        this.carryOverLegacyMarks(s);
        this.state.set(s);
        return s;
      }).catch(() => {
        this.loading = null; // try again on the next page
        return null;
      });
    }
    return this.loading;
  }

  /** One-time: tours this device already showed (old browser-only marks) are saved to the account,
   *  so existing users don't see them again. The marks are then removed from the browser. */
  private carryOverLegacyMarks(s: TourState): void {
    const found: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const m = LEGACY_KEY.exec(localStorage.key(i) ?? '');
        if (m) found.push(m[1]);
      }
    } catch { return; }
    if (!found.length) return;
    const fresh = found.filter(k => !s.seen.has(k));
    fresh.forEach(k => s.seen.add(k));
    const clear = () => found.forEach(k => { try { localStorage.removeItem(legacyKey(k)); } catch { /* ignore */ } });
    if (!fresh.length) { clear(); return; }
    this.http.post(`${AppConstants.API_URL}api/v1/users/me/tours/seen`, { keys: fresh }).subscribe({ next: clear, error: () => {} });
  }

  /** Remember on the account that this user has seen the tour (a start, finish, skip or close all count). */
  private markSeen(k: string): void {
    const s = this.state();
    if (!s || s.seen.has(k)) return;
    this.state.set({ ...s, seen: new Set([...s.seen, k]) });
    this.http.post(`${AppConstants.API_URL}api/v1/users/me/tours/seen`, { keys: [k] }).subscribe({
      // Could not save (e.g. offline): keep a browser mark; the next sign-in carries it over.
      error: () => { try { localStorage.setItem(legacyKey(k), '1'); } catch { /* ignore */ } },
    });
  }

  private stopActiveTour(): void {
    try { this.activeDriver?.destroy(); } catch { /* best-effort teardown */ }
    this.activeDriver = null;
  }

  private t(k: string): string {
    return this.translationService.instant(k);
  }

  // ─────────────────── shared driver config ────────────────────

  private async run(storageKey: string, buildSteps: () => DriveStep[], force: boolean): Promise<void> {
    // 2026-09-15: never over the sign-in / sign-up / onboarding / password pages — there is nothing
    // there to point at (a tour was seen floating over /login).
    const path = window.location.pathname || '';
    if (NO_TOUR_PATHS.some(p => path.startsWith(p))) return;
    // Wait for the user's tour state (one call per sign-in). Unknown (offline / error) = no tour —
    // missing one tour is better than showing it again to someone who already saw it.
    const st = await this.ensureLoaded();
    if (!st) return;
    // Auto tour: only while tours are switched ON and this user hasn't seen it.
    // Replay (Tour button / menu): while ON, or always for SUPER_ADMIN/ROOT.
    if (force ? !(st.enabled || st.canReplay) : (!st.enabled || st.seen.has(storageKey))) return;
    // The page may have changed while we waited — its anchors are gone.
    if ((window.location.pathname || '') !== path) return;
    const steps = buildSteps();
    // Present AND actually rendered (2026-09-15): an element hidden with display:none on this
    // screen size (e.g. the Business Partners stats tile on phones) still exists in the DOM, and
    // the tour used to spotlight an empty 0x0 spot for it. getClientRects() is empty for those.
    const visible = steps.filter(s => {
      if (!s.element) return true;
      const el = document.querySelector(s.element as string);
      return !!el && el.getClientRects().length > 0;
    });
    if (!visible.length) return;
    // Guard against a tour surviving with ONLY its anchor-less "welcome" step (every tour's
    // first step has no `element`, so it always passes the filter above regardless of page) —
    // confirmed real case: the header tour once fired on the login page with every OTHER step
    // filtered out (none of their target elements exist pre-login), leaving a single orphaned
    // welcome popover floating with nothing to actually point at. If none of the *targeted*
    // steps have a real anchor on this page, this isn't the right page for this tour at all.
    if (!visible.some(s => !!s.element)) return;
    // Mark seen immediately so closing the browser mid-tour doesn't re-show it next visit.
    // Replays (force) change nothing — the tour is already seen, or it is an admin check.
    if (!force) this.markSeen(storageKey);
    // Tear down any tour still running (e.g. a previous page's auto-tour) before starting a new
    // one — never leave two driver instances alive.
    this.stopActiveTour();
    const driverObj = driver({
      animate: true,
      smoothScroll: true,
      showProgress: true,
      progressText: this.t('tour.progress'),
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: this.t('tour.next'),
      prevBtnText: this.t('tour.prev'),
      doneBtnText: this.t('tour.done'),
      stagePadding: 6,
      stageRadius: 10,
      steps: visible,
      onDestroyed: () => { this.activeDriver = null; },
    });
    this.activeDriver = driverObj;
    driverObj.drive();
  }

  // ─────────────────── public API ──────────────────────────────

  startTour(force = false): void {
    void this.run('header', () => this.headerSteps(), force);
  }

  startFeatureTour(feature: FeatureTourKey, force = false): void {
    const builders: Record<FeatureTourKey, () => DriveStep[]> = {
      header:            () => this.headerSteps(),
      shipments:         () => this.shipmentSteps(),
      tallysheet:        () => this.tallySteps(),
      reports:           () => this.reportSteps(),
      'business-partner':() => this.bpSteps(),
      products:          () => this.productSteps(),
      'loading-site':    () => this.loadingSiteSteps(),
      users:             () => this.userSteps(),
      'dashboard-v7':          () => this.dashboardV7Steps(),
      'inventory-overview':    () => this.inventoryOverviewSteps(),
      'inventory-in-out':      () => this.inventoryInOutSteps(),
      'inventory-processing':  () => this.inventoryProcessingSteps(),
      'inventory-operators':   () => this.inventoryOperatorsSteps(),
      'rbac-settings':         () => this.rbacSettingsSteps(),
      'subscription-management': () => this.subscriptionManagementSteps(),
    };
    void this.run(feature, builders[feature], force);
  }

  /** Header "?" pulse: only while tours are ON and this user hasn't seen the main tour yet. */
  hasSeenTour(): boolean {
    const s = this.state();
    return !s || !s.enabled || s.seen.has('header');
  }

  hasSeenFeatureTour(f: FeatureTourKey): boolean {
    const s = this.state();
    return !s || s.seen.has(f);
  }

  // ─────────────────── Header steps ────────────────────────────

  private headerSteps(): DriveStep[] {
    return [
      {
        popover: {
          title: this.t('tour.header.welcome.title'),
          description: this.t('tour.header.welcome.desc'),
          align: 'center',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-tour-menu-btn',
        popover: {
          title: this.t('tour.header.menu.title'),
          description: this.t('tour.header.menu.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-tour-logo',
        popover: {
          title: this.t('tour.header.logo.title'),
          description: this.t('tour.header.logo.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-tour-language',
        popover: {
          title: this.t('tour.header.language.title'),
          description: this.t('tour.header.language.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-tour-fontsize',
        popover: {
          title: this.t('tour.header.fontSize.title'),
          description: this.t('tour.header.fontSize.desc'),
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tms-tour-theme',
        popover: {
          title: this.t('tour.header.theme.title'),
          description: this.t('tour.header.theme.desc'),
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tms-tour-darkmode',
        popover: {
          title: this.t('tour.header.darkMode.title'),
          description: this.t('tour.header.darkMode.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-tour-avatar',
        popover: {
          title: this.t('tour.header.profile.title'),
          description: this.t('tour.header.profile.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-tour-help',
        popover: {
          title: this.t('tour.header.help.title'),
          description: this.t('tour.header.help.desc'),
          side: 'bottom',
          align: 'end',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Shipments steps ─────────────────────────

  private shipmentSteps(): DriveStep[] {
    return [
      {
        element: '#tms-st-header',
        popover: {
          title: this.t('tour.shipments.header.title'),
          description: this.t('tour.shipments.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-st-new-btn',
        popover: {
          title: this.t('tour.shipments.new.title'),
          description: this.t('tour.shipments.new.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-st-filter',
        popover: {
          title: this.t('tour.shipments.filter.title'),
          description: this.t('tour.shipments.filter.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-st-table',
        popover: {
          title: this.t('tour.shipments.table.title'),
          description: this.t('tour.shipments.table.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-st-del-btn',
        popover: {
          title: this.t('tour.shipments.delete.title'),
          description: this.t('tour.shipments.delete.desc'),
          side: 'bottom',
          align: 'end',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Tally Sheet steps ───────────────────────

  private tallySteps(): DriveStep[] {
    return [
      {
        element: '#tms-tt-header',
        popover: {
          title: this.t('tour.tally.header.title'),
          description: this.t('tour.tally.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-tt-settings',
        popover: {
          title: this.t('tour.tally.settings.title'),
          description: this.t('tour.tally.settings.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-tt-grid',
        popover: {
          title: this.t('tour.tally.grid.title'),
          description: this.t('tour.tally.grid.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-tt-toolbar-export',
        popover: {
          title: this.t('tour.tally.export.title'),
          description: this.t('tour.tally.export.desc'),
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '#tms-tt-toolbar-save',
        popover: {
          title: this.t('tour.tally.save.title'),
          description: this.t('tour.tally.save.desc'),
          side: 'top',
          align: 'end',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Reports steps ───────────────────────────

  private reportSteps(): DriveStep[] {
    return [
      {
        element: '#tms-rpt-header',
        popover: {
          title: this.t('tour.reports.header.title'),
          description: this.t('tour.reports.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-rpt-filter',
        popover: {
          title: this.t('tour.reports.filter.title'),
          description: this.t('tour.reports.filter.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-rpt-gen-btn',
        popover: {
          title: this.t('tour.reports.generate.title'),
          description: this.t('tour.reports.generate.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-rpt-export',
        popover: {
          title: this.t('tour.reports.export.title'),
          description: this.t('tour.reports.export.desc'),
          side: 'bottom',
          align: 'end',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Business Partner steps ──────────────────

  private bpSteps(): DriveStep[] {
    return [
      {
        element: '#tms-bp-header',
        popover: {
          title: this.t('tour.bp.header.title'),
          description: this.t('tour.bp.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-bp-new-btn',
        popover: {
          title: this.t('tour.bp.new.title'),
          description: this.t('tour.bp.new.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-bp-stats',
        popover: {
          title: this.t('tour.bp.stats.title'),
          description: this.t('tour.bp.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-bp-filter',
        popover: {
          title: this.t('tour.bp.filter.title'),
          description: this.t('tour.bp.filter.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-bp-table',
        popover: {
          title: this.t('tour.bp.table.title'),
          description: this.t('tour.bp.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Products steps ──────────────────────────

  private productSteps(): DriveStep[] {
    return [
      {
        element: '#tms-pl-header',
        popover: {
          title: this.t('tour.products.header.title'),
          description: this.t('tour.products.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-pl-new-btn',
        popover: {
          title: this.t('tour.products.new.title'),
          description: this.t('tour.products.new.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-pl-stats',
        popover: {
          title: this.t('tour.products.stats.title'),
          description: this.t('tour.products.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-pl-table',
        popover: {
          title: this.t('tour.products.table.title'),
          description: this.t('tour.products.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Loading Site steps ──────────────────────

  private loadingSiteSteps(): DriveStep[] {
    return [
      {
        element: '#tms-lsl-header',
        popover: {
          title: this.t('tour.loadingSite.header.title'),
          description: this.t('tour.loadingSite.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-lsl-new-btn',
        popover: {
          title: this.t('tour.loadingSite.new.title'),
          description: this.t('tour.loadingSite.new.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-lsl-stats',
        popover: {
          title: this.t('tour.loadingSite.stats.title'),
          description: this.t('tour.loadingSite.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-lsl-table',
        popover: {
          title: this.t('tour.loadingSite.table.title'),
          description: this.t('tour.loadingSite.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Users steps ─────────────────────────────

  private userSteps(): DriveStep[] {
    return [
      {
        element: '#tms-usr-header',
        popover: {
          title: this.t('tour.users.header.title'),
          description: this.t('tour.users.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-usr-add-btn',
        popover: {
          title: this.t('tour.users.add.title'),
          description: this.t('tour.users.add.desc'),
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '#tms-usr-stats',
        popover: {
          title: this.t('tour.users.stats.title'),
          description: this.t('tour.users.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-usr-table',
        popover: {
          title: this.t('tour.users.table.title'),
          description: this.t('tour.users.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Dashboard V7 steps ──────────────────────

  private dashboardV7Steps(): DriveStep[] {
    return [
      {
        element: '#tms-dv7-header',
        popover: {
          title: this.t('tour.dashboardV7.header.title'),
          description: this.t('tour.dashboardV7.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-dv7-pulse',
        popover: {
          title: this.t('tour.dashboardV7.pulse.title'),
          description: this.t('tour.dashboardV7.pulse.desc'),
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tms-dv7-financial',
        popover: {
          title: this.t('tour.dashboardV7.financial.title'),
          description: this.t('tour.dashboardV7.financial.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-dv7-inventory-snapshot',
        popover: {
          title: this.t('tour.dashboardV7.inventory.title'),
          description: this.t('tour.dashboardV7.inventory.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-dv7-recon',
        popover: {
          title: this.t('tour.dashboardV7.recon.title'),
          description: this.t('tour.dashboardV7.recon.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-dv7-charts',
        popover: {
          title: this.t('tour.dashboardV7.charts.title'),
          description: this.t('tour.dashboardV7.charts.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-dv7-activity',
        popover: {
          title: this.t('tour.dashboardV7.activity.title'),
          description: this.t('tour.dashboardV7.activity.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Inventory Overview steps ────────────────

  private inventoryOverviewSteps(): DriveStep[] {
    return [
      {
        element: '#tms-invov-header',
        popover: {
          title: this.t('tour.inventoryOverview.header.title'),
          description: this.t('tour.inventoryOverview.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-invov-actions',
        popover: {
          title: this.t('tour.inventoryOverview.actions.title'),
          description: this.t('tour.inventoryOverview.actions.desc'),
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '#tms-invov-lots',
        popover: {
          title: this.t('tour.inventoryOverview.lots.title'),
          description: this.t('tour.inventoryOverview.lots.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Inventory In-Out steps ──────────────────

  private inventoryInOutSteps(): DriveStep[] {
    return [
      {
        element: '#tms-invio-header',
        popover: {
          title: this.t('tour.inventoryInOut.header.title'),
          description: this.t('tour.inventoryInOut.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-invio-filter',
        popover: {
          title: this.t('tour.inventoryInOut.filter.title'),
          description: this.t('tour.inventoryInOut.filter.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-invio-table',
        popover: {
          title: this.t('tour.inventoryInOut.table.title'),
          description: this.t('tour.inventoryInOut.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Inventory Processing steps ──────────────

  private inventoryProcessingSteps(): DriveStep[] {
    return [
      {
        element: '#tms-invpr-header',
        popover: {
          title: this.t('tour.inventoryProcessing.header.title'),
          description: this.t('tour.inventoryProcessing.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-invpr-stats',
        popover: {
          title: this.t('tour.inventoryProcessing.stats.title'),
          description: this.t('tour.inventoryProcessing.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-invpr-table',
        popover: {
          title: this.t('tour.inventoryProcessing.table.title'),
          description: this.t('tour.inventoryProcessing.table.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Inventory Operators steps ───────────────

  private inventoryOperatorsSteps(): DriveStep[] {
    return [
      {
        element: '#tms-invop-header',
        popover: {
          title: this.t('tour.inventoryOperators.header.title'),
          description: this.t('tour.inventoryOperators.header.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-invop-stats',
        popover: {
          title: this.t('tour.inventoryOperators.stats.title'),
          description: this.t('tour.inventoryOperators.stats.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-invop-search',
        popover: {
          title: this.t('tour.inventoryOperators.search.title'),
          description: this.t('tour.inventoryOperators.search.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── RBAC Settings steps ─────────────────────

  private rbacSettingsSteps(): DriveStep[] {
    return [
      {
        element: '#tms-rbac-toggle',
        popover: {
          title: this.t('tour.rbacSettings.toggle.title'),
          description: this.t('tour.rbacSettings.toggle.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-rbac-tabs',
        popover: {
          title: this.t('tour.rbacSettings.tabs.title'),
          description: this.t('tour.rbacSettings.tabs.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-rbac-users-table',
        popover: {
          title: this.t('tour.rbacSettings.usersTable.title'),
          description: this.t('tour.rbacSettings.usersTable.desc'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '#tms-rbac-roles-tab',
        popover: {
          title: this.t('tour.rbacSettings.rolesTab.title'),
          description: this.t('tour.rbacSettings.rolesTab.desc'),
          side: 'top',
          align: 'center',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }

  // ─────────────────── Subscription Management steps ───────────

  private subscriptionManagementSteps(): DriveStep[] {
    return [
      {
        element: '#tms-sub-accordion',
        popover: {
          title: this.t('tour.subscriptionManagement.accordion.title'),
          description: this.t('tour.subscriptionManagement.accordion.desc'),
          side: 'bottom',
          align: 'start',
          popoverClass: 'tms-tour-welcome',
        },
      },
      {
        element: '#tms-sub-package-select',
        popover: {
          title: this.t('tour.subscriptionManagement.packageSelect.title'),
          description: this.t('tour.subscriptionManagement.packageSelect.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-sub-billing',
        popover: {
          title: this.t('tour.subscriptionManagement.billing.title'),
          description: this.t('tour.subscriptionManagement.billing.desc'),
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '#tms-sub-pay',
        popover: {
          title: this.t('tour.subscriptionManagement.pay.title'),
          description: this.t('tour.subscriptionManagement.pay.desc'),
          side: 'top',
          align: 'end',
          popoverClass: 'tms-tour-done',
        },
      },
    ];
  }
}
